import React, { useMemo, useState } from 'react';
import {
    mdiArchive,
    mdiArchiveOutline,
    mdiCardAccountDetailsOutline,
    mdiFolder,
    mdiFolderOutline,
    mdiMenuDown,
    mdiMenuRight,
    mdiPound,
    mdiUnfoldLessHorizontal,
    mdiUnfoldMoreHorizontal,
} from '@mdi/js';
import { Box, Button, Stack, SxProps } from '@mui/material';
import { vars } from '../../../../Theme';
import SearchField from '../UploadDialog/_parts/SearchField';
import { CheckboxTreeItem } from './CheckboxTreeItem';
import { StructureViewMode } from '../../../../types';
import { SimpleTreeView } from '@mui/x-tree-view';
import IconWrapper from '../../Icons/IconWrapper';
import { useTranslation } from 'react-i18next';
import Icon from '@mdi/react';

export const colorConfig = {
    Category: vars.tagChipBlue,
    Package: vars.brandOrange,
    Skeleton: vars.darkGray,
    Keyword: vars.brandDarkBlue,
    Address: vars.trafficLightGreen,
};

export const iconConfig = {
    Category: {
        parentIcon: {
            path: mdiFolder,
            color: colorConfig.Category,
        },
        childIcon: {
            path: mdiFolderOutline,
            color: colorConfig.Category,
        },
        skeletonIcon: {
            path: mdiFolder,
            color: colorConfig.Skeleton,
        },
    },
    Package: {
        parentIcon: {
            path: mdiArchive,
            color: colorConfig.Package,
        },
        childIcon: {
            path: mdiArchiveOutline,
            color: colorConfig.Package,
        },
        skeletonIcon: {
            path: mdiArchive,
            color: colorConfig.Skeleton,
        },
    },
    Keyword: {
        parentIcon: {
            path: mdiPound,
            color: colorConfig.Keyword,
        },
        childIcon: {
            path: mdiPound,
            color: colorConfig.Keyword,
        },
        skeletonIcon: {
            path: mdiPound,
            color: colorConfig.Skeleton,
        },
    },
    Address: {
        parentIcon: {
            path: mdiCardAccountDetailsOutline,
            color: colorConfig.Address,
        },
        childIcon: {
            path: mdiCardAccountDetailsOutline,
            color: colorConfig.Address,
        },
        skeletonIcon: {
            path: mdiCardAccountDetailsOutline,
            color: colorConfig.Skeleton,
        },
    },
};

const expandButtonSx: SxProps = {
    color: vars.font,
    height: '36px',
    minWidth: 'auto',
    padding: '6px',
};

const treeActionBarSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
};

const labelComparer = (a: { label: string }, b: { label: string }) => {
    if (a.label === b.label) {
        return 0;
    }
    return a.label > b.label ? 1 : -1;
};

const getTreeItem = (item: ICheckboxItemProps, all: ICheckboxItemProps[]) => {
    const children = all
        .filter(x => x.parent === item.path)
        .sort(labelComparer)
        .map(x => getTreeItem(x, all));

    return {
        ...item,
        children: children,
    };
};

const collectAncestorItems = (
    allItems: ICheckboxItemProps[],
    results: ICheckboxItemProps[],
    item: ICheckboxItemProps,
) => {
    if (item.parent) {
        const parent = allItems.find(x => x.path === item.parent);

        if (parent) {
            results.push(parent);

            collectAncestorItems(allItems, results, parent);
        }
    }
};

const collectDecestorItems = (
    allItems: ICheckboxItemProps[],
    results: ICheckboxItemProps[],
    item: ICheckboxItemProps,
) => {
    const child = allItems.find(x => x.parent === item.path);

    if (child) {
        results.push(child);

        collectDecestorItems(allItems, results, child);
    }
};

const buildTree = (all: ICheckboxItemProps[]) => {
    const rootItems = all.filter(item => !item.parent).sort(labelComparer);

    return rootItems.map(item => getTreeItem(item, all));
};

interface ICheckboxItemProps {
    id: string;
    label: string;
    path: string;
    parent: string;
    isSkeleton?: boolean;
    icon?: {
        childIcon: {
            path: string;
            color: string;
        };
    };
    hasCheckbox?: boolean;
}

interface ICheckboxTreeProps {
    items: ICheckboxItemProps[];
    checkedNodes: string[];
    halfCheckedNodes?: string[];
    onCheckNode?: (id: string, isChecked: boolean, tree?: any) => void;
    withStructureIcons?: StructureViewMode;
    simpleTreeViewSx?: SxProps;
    hasError?: boolean;
    withSearch?: boolean;
    withTreeExpansion?: boolean;
}

export default ({
    items,
    checkedNodes,
    onCheckNode,
    halfCheckedNodes = [],
    withStructureIcons: structureMode,
    simpleTreeViewSx,
    hasError = false,
    withSearch = true,
    withTreeExpansion = false,
}: ICheckboxTreeProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'checkboxTree' });

    const [searchText, setSearchText] = useState<string>('');
    const [expandedItems, setExpandedItems] = useState<string[]>([]);

    const filteredItems = useMemo(() => {
        if (!searchText) {
            return items;
        }

        const filtered = items.filter(x =>
            x.label.toLowerCase().includes(searchText.toLowerCase()),
        );

        const relatedItems: ICheckboxItemProps[] = [];
        filtered.forEach(x => collectAncestorItems(items, relatedItems, x));
        filtered.forEach(x => collectDecestorItems(items, relatedItems, x));

        const results: ICheckboxItemProps[] = [];

        [...filtered, ...relatedItems].forEach(x => {
            if (!results.some(existing => existing.path === x.path)) {
                results.push(x);
            }
        });

        return results;
    }, [items, searchText]);

    const tree = buildTree(filteredItems);

    const documentTypesTree = useMemo(() => {
        if (tree?.length) {
            return tree.map(item => (
                <CheckboxTreeItem
                    key={item.id ?? item.path}
                    id={item.id ?? item.path}
                    label={item.label}
                    isChecked={id =>
                        halfCheckedNodes.includes(id) ? 'halfChecked' : checkedNodes.includes(id)
                    }
                    onCheck={(id, isChecked) => {
                        onCheckNode(id, isChecked, tree);
                    }}
                    iconConfig={item.icon || iconConfig[structureMode]}
                    isSkeleton={item.isSkeleton}
                    hasCheckbox={item.hasCheckbox}
                    chipLabel={item.chipLabel}
                >
                    {item.children}
                </CheckboxTreeItem>
            ));
        } else {
            return (
                <CheckboxTreeItem
                    key={'notFound'}
                    id={'notFound'}
                    label={t('notFound')}
                    notFound={true}
                />
            );
        }
    }, [tree]);

    const handleExpandedItemsChange = (_: React.SyntheticEvent, itemIds: string[]) => {
        setExpandedItems(itemIds);
    };

    const handleExpandClick = () => {
        setExpandedItems(items.map(item => item.id));
    };

    const handleCollapseClick = () => {
        setExpandedItems([]);
    };

    return (
        <Stack sx={{ width: '100%' }}>
            <Stack sx={treeActionBarSx}>
                {withSearch && <SearchField onChange={newValue => setSearchText(newValue)} />}
                {withTreeExpansion && (
                    <Stack
                        sx={{
                            ...treeActionBarSx,
                            alignItems: 'center',
                        }}
                    >
                        <Button onClick={handleExpandClick} sx={expandButtonSx}>
                            <Icon path={mdiUnfoldMoreHorizontal} size={1} />
                        </Button>
                        <Button onClick={handleCollapseClick} sx={expandButtonSx}>
                            <Icon path={mdiUnfoldLessHorizontal} size={1} />
                        </Button>
                    </Stack>
                )}
            </Stack>
            <Box
                sx={{
                    borderRadius: '4px',
                    border: `1px solid ${hasError ? vars.errorDialogRed : vars.lightGray}`,
                    marginTop: '8px',
                }}
            >
                <SimpleTreeView
                    expandedItems={expandedItems}
                    onExpandedItemsChange={handleExpandedItemsChange}
                    sx={{ overflowY: 'auto', ...simpleTreeViewSx }}
                    multiSelect={false}
                    defaultExpandedItems={['root']}
                    disableSelection
                    slots={{
                        expandIcon: IconWrapper({ path: mdiMenuRight }),
                        collapseIcon: IconWrapper({ path: mdiMenuDown }),
                    }}
                    itemChildrenIndentation={16}
                >
                    {documentTypesTree}
                </SimpleTreeView>
            </Box>
        </Stack>
    );
};
